import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

interface AuthTokenPair {
  readonly access_token: string;
  readonly refresh_token: string;
}

interface JwtPayload {
  readonly exp?: number;
}

const accessTokenStorageKey = 'espresso.auth.access-token';
const refreshTokenStorageKey = 'espresso.auth.refresh-token';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private readonly document = inject(DOCUMENT);

  hasAccessToken(): boolean {
    return !!this.getAccessToken();
  }

  hasRefreshToken(): boolean {
    return !!this.getRefreshToken();
  }

  getAccessToken(): string | null {
    return this.getStorage()?.getItem(accessTokenStorageKey) ?? null;
  }

  getRefreshToken(): string | null {
    return this.getStorage()?.getItem(refreshTokenStorageKey) ?? null;
  }

  setTokens(tokens: AuthTokenPair): void {
    const storage = this.getStorage();

    if (!storage) {
      return;
    }

    storage.setItem(accessTokenStorageKey, tokens.access_token);
    storage.setItem(refreshTokenStorageKey, tokens.refresh_token);
  }

  clearTokens(): void {
    const storage = this.getStorage();

    if (!storage) {
      return;
    }

    storage.removeItem(accessTokenStorageKey);
    storage.removeItem(refreshTokenStorageKey);
  }

  isAccessTokenExpiringSoon(bufferInSeconds = 60): boolean {
    const accessToken = this.getAccessToken();

    if (!accessToken) {
      return false;
    }

    return this.isTokenExpired(accessToken, bufferInSeconds);
  }

  private isTokenExpired(token: string, bufferInSeconds = 0): boolean {
    const payload = this.parseJwtPayload(token);

    if (!payload?.exp) {
      return false;
    }

    const nowInSeconds = Math.floor(Date.now() / 1000);

    return payload.exp <= nowInSeconds + bufferInSeconds;
  }

  private parseJwtPayload(token: string): JwtPayload | null {
    try {
      const payload = token.split('.')[1];

      if (!payload) {
        return null;
      }

      const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
      const paddedPayload = normalizedPayload.padEnd(
        Math.ceil(normalizedPayload.length / 4) * 4,
        '=',
      );

      return JSON.parse(atob(paddedPayload)) as JwtPayload;
    } catch {
      return null;
    }
  }

  private getStorage(): Storage | null {
    try {
      return this.document.defaultView?.localStorage ?? null;
    } catch {
      return null;
    }
  }
}
